/**
 * Copyright © 2026 Adnuntius AS.
 */
import {AdnListHelperService} from '../common/controller/list-helper.service';
import {AppLocaleService} from '../common/locale/app-locale.service';
import {compareById} from '../common/util/object-id-comparison';
import {ChangeDetectorRef, DOCUMENT, Component, Inject, Input, NgZone, OnDestroy, signal} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {NgForm} from '@angular/forms';
import * as _ from 'lodash';
import {AppEventBusService} from '../common/events/app-event-bus.service';

import template from './line-item-edit-page.component.html';
import {AdminResourceEditPageBase} from '../common/controller/admin-resource-edit-page-base';
import {
  clearFormValidation,
  handleApiValidationErrors,
  markFormSubmitError,
  markFormSubmitSuccess,
  updateFormWarnings
} from '../common/controller/form-submit-state';
import {applyResourceStateFromApi} from '../common/controller/model-comms-actions';
import {LineItemCpmFeesModalService} from './line-item-cpm-fees-modal.service';
import {CreativeFromLineItemCopyModalService} from './creative-from-line-item-copy-modal.service';
import {MpProductModalService} from './mp-product-modal.service';
import {OrderBudgetModalService} from '../order/order-budget-modal.service';
import {CreativePreviewModalService} from '../creative/creative-preview-modal.service';
import {LineItemRetailProductModalService} from './line-item-retail-product-modal.service';
import {CreativeBulkActionService} from '../creative/creative-bulk-action.service';
import {BurnRatesResource} from '../../components/api/resources/resource/stats-resources/burn-rates-resource';
import {StatsResource} from '../../components/api/resources/resource/stats-resources/stats-resource';
import {CreativeCopyInBulkModalService} from '../creative/creative-copy-in-bulk-modal.service';
import {TargetingEditorModalService} from '../targeting/targeting-editor-modal.service';
import {setupLineItemEditCtrl} from './line-item-edit-setup';
import {UpdateSession} from '../../components/session/update-session';
import {Uuid} from '../../components/util/uuid';
import {IAB_TAXONOMY} from '../../components/util/iab-taxonomy';
import {isIabCategoryEnabledForNetwork} from './iab-category-network';
import {AdnLogService} from '../../components/util/adn-log.service';
import {OrderResource} from '../../components/api/resources/resource/order.resource';
import {TeamResource} from '../../components/api/resources/resource/team.resource';
import {AdvertiserResource} from '../../components/api/resources/resource/advertiser.resource';
import {CreativeResource} from '../../components/api/resources/resource/creative.resource';
import {CustomEventResource} from '../../components/api/resources/resource/custom-event.resource';
import {LineItemResource} from '../../components/api/resources/resource/line-item.resource';
import {MarketplaceProductOptionsResource} from '../../components/api/resources/resource/marketplace-product.resource';
import {MarketplaceProductResource} from '../../components/api/resources/resource/marketplace-product.resource';
import {NetworkDataResource} from '../../components/api/resources/resource/network-data.resource';
import {ProductFeedResource} from '../../components/api/resources/resource/product-feed.resource';
import {PublicPoliticalCampaignResource} from '../../components/api/resources/resource/political-campaign-public.resource';
import {AdnInactiveTargetsService} from '../../components/util/adn-inactive-targets.service';
import {AdnModalLoadingService} from '../common/util/adn-modal-loading.service';
import {AdnRollbarService} from '../../components/util/adn-rollbar.service';
import {AdnTimeShifterService} from '../../components/util/adn-time-shifter.service';
import {DataViewResource} from '../../components/api/resources/resource/data-view';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {BrowserLocationService} from '../angular-platform-providers';
import {ADN_STAT_FILTER} from '../common/filters/adn-stat-filter';
import {bufferTemplateValue, createBufferedValue} from '../common/util/template-state';
import {StateService} from '@uirouter/core';

@Component({
  standalone: false,
  selector: 'adn-line-item-edit-page',
  template
})
export class LineItemEditPageComponent extends AdminResourceEditPageBase implements OnDestroy {
  [key: string]: any;

  private _readOnlyDirect: boolean | undefined;
  private _fullDirect: boolean | undefined;
  private _viewTypeDirect: number | undefined;
  private _orderFromParamDirect: any;
  private _advertiserFromParamDirect: any;
  private _lineItemViewDirect: any;
  private setupInitialized = false;
  private destroyCallbacks: Array<() => void> = [];
  private activeForm: NgForm | null = null;
  private initDirtyResumeScheduled = false;

  /** Eager copy so the IAB <select> options exist before first CD (avoids clearing ngModel). */
  iabTaxonomy: any[] = _.cloneDeep(IAB_TAXONOMY);
  uiFees: any = {fees: []};
  showIabCategory = false;
  showIabCategoryOnNetwork = false;

  /** Async / visibility view state — signals replace deferMarkForCheck plain writes. */
  readonly marketplaceResolved = signal(false);
  readonly teams = signal<any[]>([]);
  readonly customEvents = signal<any[] | undefined>(undefined);
  readonly showPublisherApprovals = signal(false);
  readonly publisherApprovals = signal<any[]>([]);
  readonly publisherApprovalsLoaded = signal(false);
  readonly publisherApprovalsLoading = signal(false);
  readonly publisherApprovalsError = signal(false);
  readonly showBidStrategy = signal(false);
  readonly showVCpmStrategy = signal(false);
  readonly showDynamicCpmStrategy = signal(false);
  readonly showFloorBidStrategy = signal(false);
  readonly rateLimitReadOnly = signal(false);
  readonly isLockedDown = signal(false);
  readonly showObjectives = signal(false);
  readonly singleObjective = signal(false);
  readonly singleBudgetObjective = signal(false);
  readonly currentBurnRate = signal<number | undefined>(undefined);
  readonly hasStats = signal(false);

  /**
   * Phased init query for adn-promise-spinner. Stays {$resolved:false} through
   * seeding/hydrating so marketplace-dependent sections are out of CD; replaced once at ready.
   * `editPhase` / `editPhaseApi` are attached in setupLineItemEditCtrl.
   */
  editPhaseQuery: {$resolved: boolean} = {$resolved: false};
  editPhase: any;
  editPhaseApi: any;

  /**
   * Buffers !isAxAdvertiser || !!axProduct — marketplace hydrate assigns axProduct
   * mid-CD (false→true NG0100 on the delivery/objectives section *ngIf).
   */
  private readonly _showMainSectionsFlag = {display: false, last: undefined as boolean | undefined};

  /**
   * objectivesAsString() rebuilds from live objectives + network translations.
   * Product hydrate / profile translations can flip
   * "780 000 impressions" → "780 000 rendered impressions" mid-check (NG0100).
   */
  private readonly _objectivesAsString = createBufferedValue('');

  /**
   * Marketplace hydrate replaces axProduct with the catalog copy mid-CD
   * ("Gold Banner…" → "Category Product List…") on the product name span.
   */
  private readonly _axProductName = createBufferedValue('');
  private readonly _productLabel = createBufferedValue('');

  get displayShowMainSections(): boolean {
    return this.bufferTemplateFlag(
      this._showMainSectionsFlag,
      !this.isAxAdvertiser || !!this.editingModel?.axProduct
    );
  }

  get displayObjectivesAsString(): string {
    const next = typeof this.objectivesAsString === 'function' ? this.objectivesAsString() : '';
    return bufferTemplateValue(this._objectivesAsString, next, {
      onCommit: () => this.cdr.markForCheck()
    });
  }

  get displayAxProductName(): string {
    return bufferTemplateValue(this._axProductName, this.editingModel?.axProduct?.name || '', {
      onCommit: () => this.cdr.markForCheck()
    });
  }

  get displayProductLabel(): string {
    const next = this.productString || this.editingModel?.axProduct?.name || 'No type has been selected';
    return bufferTemplateValue(this._productLabel, next, {
      onCommit: () => this.cdr.markForCheck()
    });
  }

  @Input()
  set readOnly(value: boolean) {
    this._readOnlyDirect = value;
  }

  get readOnly(): boolean {
    return this._readOnlyDirect;
  }

  @Input()
  set full(value: boolean) {
    this._fullDirect = value;
  }

  get full(): boolean {
    return this._fullDirect;
  }

  @Input()
  set viewType(value: number) {
    this._viewTypeDirect = value;
  }

  get viewType(): number {
    return this._viewTypeDirect;
  }

  @Input()
  set orderFromParam(value: any) {
    this._orderFromParamDirect = value;
  }

  get orderFromParam(): any {
    return this._orderFromParamDirect;
  }

  @Input()
  set lineItemView(value: any) {
    this._lineItemViewDirect = value;
  }

  get lineItemView(): any {
    return this._lineItemViewDirect;
  }

  @Input()
  set advertiserFromParam(value: any) {
    this._advertiserFromParamDirect = value;
  }

  get advertiserFromParam(): any {
    return this._advertiserFromParamDirect;
  }

  constructor(
    eventBus: AppEventBusService,
    @Inject(UserProfileSaverService) UserProfileSaver: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    private readonly $state: StateService,
    @Inject(BrowserLocationService) private readonly location: any,
    private readonly adnLog: AdnLogService,
    @Inject(DOCUMENT) private readonly document: any,
    private readonly appLocale: AppLocaleService,
    private readonly translate: TranslateService,
    @Inject(AdnInactiveTargetsService) private readonly adnInactiveTargets: any,
    @Inject(ProductFeedResource) private readonly ProductFeed: any,
    @Inject(LineItemResource) private readonly LineItem: any,
    @Inject(AdvertiserResource) private readonly Advertiser: any,
    @Inject(PublicPoliticalCampaignResource) private readonly PublicPoliticalCampaign: any,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    private readonly burnRatesResource: BurnRatesResource,
    @Inject(MarketplaceProductOptionsResource) private readonly MarketplaceProductOptions: any,
    @Inject(NetworkDataResource) private readonly NetworkData: any,
    @Inject(AdnModalLoadingService) private readonly adnModalLoading: any,
    private readonly adnListHelper: AdnListHelperService,
    @Inject(TeamResource) private readonly Team: any,
    @Inject(ADN_STAT_FILTER) private readonly adnStatFilter: any,
    @Inject(AdnRollbarService) private readonly Rollbar: any,
    @Inject(OrderResource) private readonly Order: any,
    @Inject(MarketplaceProductResource) private readonly MarketplaceProduct: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    private readonly statsResource: StatsResource,
    @Inject(CustomEventResource) private readonly CustomEvent: any,
    @Inject(DataViewResource) private readonly dataViewResource: any,
    @Inject(CreativeResource) private readonly Creative: any,
    @Inject(AdnTimeShifterService) private readonly adnTimeShifter: any,
    private readonly targetingEditorModalService: TargetingEditorModalService,
    private readonly lineItemCpmFeesModalService: LineItemCpmFeesModalService,
    private readonly creativeFromLineItemCopyModalService: CreativeFromLineItemCopyModalService,
    private readonly mpProductModalService: MpProductModalService,
    private readonly orderBudgetModalService: OrderBudgetModalService,
    private readonly creativePreviewModalService: CreativePreviewModalService,
    private readonly lineItemRetailProductModalService: LineItemRetailProductModalService,
    private readonly creativeBulkActionService: CreativeBulkActionService,
    private readonly creativeCopyInBulkModalService: CreativeCopyInBulkModalService,
    private readonly cdr: ChangeDetectorRef,
    ngZone: NgZone
  ) {
    super(eventBus, UserProfileSaver, LocalNetworkProfile, ngZone);
    this.deferFormDirtyTracking();
    this.showIabCategoryOnNetwork = isIabCategoryEnabledForNetwork(LocalNetworkProfile.getNetworkTag());
    this.showIabCategory = LocalNetworkProfile.getDefaults().iabCategoryOnLineItem === 'SHOW';
  }

  get creativeListHost(): this {
    return this;
  }

  readonly compareById = compareById;

  ensureCpaStructure(): void {
    if (!this.editingModel) {
      return;
    }
    this.editingModel.bidSpecification = this.editingModel.bidSpecification || {};
    this.editingModel.bidSpecification.cpa = this.editingModel.bidSpecification.cpa || {};
    this.editingModel.bidSpecification.cpa.cost = this.editingModel.bidSpecification.cpa.cost || {};
    this.editingModel.bidSpecification.cpa.initialECpm = this.editingModel.bidSpecification.cpa.initialECpm || {};
  }

  onShowCpaChange(show: boolean): void {
    if (show) {
      this.ensureCpaStructure();
    }
    this.showCpa = show;
  }

  onFieldChange(): void {
    this.markExternalFormDirty();
  }

  onUserFieldInput(): void {
    this.markExternalFormDirty(true);
  }

  editsMade(): void {
    // Match order/advertiser: ignore programmatic calls while init is settling.
    this.markExternalFormDirty();
  }

  /**
   * Resume dirty tracking once async init (marketplace product hydrate, etc.)
   * has finished writing into the form. Safe to call multiple times.
   */
  scheduleResumeFormDirtyTrackingAfterInit(): void {
    if (this.initDirtyResumeScheduled) {
      return;
    }
    this.initDirtyResumeScheduled = true;
    setTimeout(() => {
      this.resumeFormDirtyTracking();
    }, 500);
  }

  protected afterInitEditingModel(): void {
    this.initDirtyResumeScheduled = false;
    this.ensureSetup();
    this._lineItemAfterInit?.();
    // Fallback if marketplace resolution never signals (e.g. aborted navigation).
    setTimeout(() => {
      this.scheduleResumeFormDirtyTrackingAfterInit();
    }, 2500);
  }

  protected afterInitAfterSave(saveObj?: any): void {
    this._lineItemAfterInitAfterSave?.(saveObj);
    this._ensureElectionModel?.();
  }

  protected applySave(resource: any): void {
    if (resource) {
      _.merge(this.editingModel, resource);
      Object.assign(this.model, resource);
      applyResourceStateFromApi(this.editingModel, resource);
      applyResourceStateFromApi(this.model, resource);
    }
    this.isNew = false;
    this.publishModelUpdate();
    if (this.showCpa) {
      this.ensureCpaStructure();
    }
    this._lineItemAfterSave?.(resource);
  }

  protected onBeforeSubmit(): void {
    this.submitState.clientErrors = [];
    this._lineItemOnBeforeSubmit?.();
  }

  get submitBlocked(): boolean {
    return !!(this.blockSubmit || this.costFailsafe);
  }

  submit(editForm: NgForm, options?: {skipPristine?: boolean}): void {
    const blocked = options?.skipPristine
      ? (this.pending || this.submitBlocked || (editForm.invalid && !this.hasValidationError))
      : this.isSaveDisabled(editForm, this.submitBlocked);
    if (blocked || !_.trim(this.editingModel?.name || '')) {
      return;
    }

    this.pending = true;
    this.submitState.validSubmission = false;
    clearFormValidation(this.submitState, this.formControlErrors);

    this.onBeforeSubmit();

    if (this.submitState.clientErrors?.length > 0) {
      handleApiValidationErrors(this.submitState, this.editingModel, this.submitState.clientErrors, this.formControlErrors,
        editForm
      );
      this.submitState.clientErrors = [];
      markFormSubmitError(this.submitState, editForm);
      this.pending = false;
      this.eventBus.emit('SUBMIT_PROCESSED');
      return;
    }

    if (!this.editingModel.id) {
      this.editingModel.id = Uuid.generate();
    }

    this.syncEditingModelToApiModel();
    const saveTarget = _.isFunction(this.model.savePartial) ? this.model : this.editingModel;
    const endPoint = this.endPoint || 'save';

    saveTarget.savePartial(null, endPoint, null).then((apiResource: any) => {
      this.ngZone.run(() => {
        this.pending = false;
        this.applySave(apiResource);
        this.submitState.ngForm = editForm;
        updateFormWarnings(this.submitState, this.getWarningModel(), this.LocalNetworkProfile.isPureMarketplacePlatform());
        if (!UpdateSession.obtainData().authToken) {
          this.UserProfileSaver.saveObj(apiResource, 'adnLastEdited');
          this.UserProfileSaver.saveObj(apiResource, 'adnLastViewed');
        }
        markFormSubmitSuccess(this.submitState, editForm);
        this.externalFormDirty = false;
        this.resumeFormDirtyTracking();
      });
    }, (response: any) => {
      this.pending = false;
      if ((response.status === 400 || response.status === 403 || response.status === 422)
        && (response.data.type === 'ValidationResult' || response.data.type === 'ErrorMessage')) {
        handleApiValidationErrors(
          this.submitState,
          this.editingModel,
          _.get(response, ['data', 'errors']) || response.data,
          this.formControlErrors,
          editForm
        );
      }
      markFormSubmitError(this.submitState, editForm);
    }).finally(() => {
      this.pending = false;
      this.eventBus.emit('SUBMIT_PROCESSED');
    });
  }

  submitWizard(wizardForm: NgForm): void {
    this.onSaveAndNext?.();
    // Wizard Save and Next is always intentional. The main edit form's pristine
    // gate would block here: the wizard form is not dirty-tracked the same way,
    // and product/country thumbnail clicks do not mark the form dirty.
    this.submit(wizardForm, {skipPristine: true});
  }

  registerForm(editForm: NgForm): void {
    super.registerForm(editForm);
    this.activeForm = editForm;
  }

  getForm(): NgForm | null {
    return this.activeForm;
  }

  ngOnDestroy(): void {
    _.forEach(this.destroyCallbacks, (fn) => fn());
    super.ngOnDestroy();
  }

  private ensureSetup(): void {
    if (this.setupInitialized || !this.model) {
      return;
    }
    this.setupInitialized = true;

    setupLineItemEditCtrl(this, {
      model: this.model,
      readOnly: this.readOnly,
      full: this.full,
      viewType: this.viewType,
      adnInactiveTargets: this.adnInactiveTargets,
      ProductFeed: this.ProductFeed,
      LocalNetworkProfile: this.LocalNetworkProfile,
      location: this.location,
      LineItem: this.LineItem,
      Advertiser: this.Advertiser,
      PublicPoliticalCampaign: this.PublicPoliticalCampaign,
      localUserProfile: this.localUserProfile,
      onDestroy: (cleanup) => this.destroyCallbacks.push(cleanup),
      eventBus: this.eventBus,
      burnRatesResource: this.burnRatesResource,
      MarketplaceProductOptions: this.MarketplaceProductOptions,
      NetworkData: this.NetworkData,
      adnLog: this.adnLog,
      orderFromParam: this.orderFromParam,
      advertiserFromParam: this.advertiserFromParam,
      adnModalLoading: this.adnModalLoading,
      modelComms: this.modelComms,
      adnListHelper: this.adnListHelper,
      Team: this.Team,
      adnStatFilter: this.adnStatFilter,
      document: this.document,
      Rollbar: this.Rollbar,
      Order: this.Order,
      MarketplaceProduct: this.MarketplaceProduct,
      LocalUserPermissions: this.LocalUserPermissions,
      lineItemView: this.lineItemView,
      statsResource: this.statsResource,
      CustomEvent: this.CustomEvent,
      cdr: this.cdr,
      dataViewResource: this.dataViewResource,
      appLocale: this.appLocale,
      translate: this.translate,
      Creative: this.Creative,
      adnTimeShifter: this.adnTimeShifter,
      TargetingEditorModalService: this.targetingEditorModalService,
      $state: this.$state,
      lineItemCpmFeesModalService: this.lineItemCpmFeesModalService,
      creativeFromLineItemCopyModalService: this.creativeFromLineItemCopyModalService,
      mpProductModalService: this.mpProductModalService,
      orderBudgetModalService: this.orderBudgetModalService,
      creativePreviewModalService: this.creativePreviewModalService,
      lineItemRetailProductModalService: this.lineItemRetailProductModalService,
      creativeBulkActionService: this.creativeBulkActionService,
      creativeCopyInBulkModalService: this.creativeCopyInBulkModalService
    });
  }
}
